import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  PencilSimpleLineIcon,
  PlusIcon,
  ArrowRightIcon,
  CaretDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import MySolutionCard from './community/MySolutionCard';
import OtherSolutionCard from './community/OtherSolutionCard';
import BlockedBanner from './community/BlockedBanner';
import AddEditSolutionModal from './community/AddEditSolutionModal';
import GuidelinesModal from './community/GuidelinesModal';
import ReportSolutionModal from './community/ReportSolutionModal';
import { getFileExtensionForBlob } from './community/imageProcessing';

const BASE_URL = '/api/v1/community-solution';
const PAGE_SIZE = 10;
const LIST_MAX_HEIGHT = 651;

const CommunitySolutionPanel = ({ questionId, user, questionMeta }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [mySolution, setMySolution] = useState(null);
  const [hasWriteAccess, setHasWriteAccess] = useState(true);
  const [writeAccessBlockReason, setWriteAccessBlockReason] = useState(null);
  const [others, setOthers] = useState([]);
  const [othersTotal, setOthersTotal] = useState(0);
  const [sortBy, setSortBy] = useState('votes');
  const [page, setPage] = useState(1);
  const [hasScrolledList, setHasScrolledList] = useState(false);
  const [canScrollList, setCanScrollList] = useState(false);
  const listRef = useRef(null);

  // The "Scroll To view more" hint should only appear when the list actually
  // overflows — re-check on every resize (images inside cards load async and
  // change the scroll height after the initial render).
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const checkOverflow = () => setCanScrollList(el.scrollHeight > el.clientHeight + 1);
    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [others]);

  const [metaData, setMetaData] = useState(null);
  const [showGuidelines, setShowGuidelines] = useState(false);

  const [modalMode, setModalMode] = useState(null); // 'new' | 'edit' | null
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [reportSolutionId, setReportSolutionId] = useState(null);
  const [isReporting, setIsReporting] = useState(false);

  const fetchMySolution = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/${questionId}/my-solution`);
      const writeAccess = res?.data?.data?.hasWriteAccess;
      setMySolution(res?.data?.data?.solution ?? null);
      setHasWriteAccess(!writeAccess?.uploadBlocked);
      setWriteAccessBlockReason(writeAccess?.reason ?? null);
    } catch (error) {
      setMySolution(null);
      setHasWriteAccess(true);
      setWriteAccessBlockReason(null);
    }
  };

  const fetchOthers = async (nextSortBy = sortBy, nextPage = page) => {
    try {
      const res = await axios.get(`${BASE_URL}/${questionId}`, {
        params: { sortBy: nextSortBy, limit: PAGE_SIZE, offset: (nextPage - 1) * PAGE_SIZE },
      });
      setOthers(res?.data?.data?.solutions ?? []);
      setOthersTotal(res?.data?.data?.total ?? 0);
    } catch (error) {
      setOthers([]);
      setOthersTotal(0);
    }
    setHasScrolledList(false);
    if (listRef.current) listRef.current.scrollTop = 0;
  };

  const fetchMetaData = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/meta-data`);
      setMetaData(res?.data?.data ?? null);
    } catch (error) {
      setMetaData(null);
    }
  };

  useEffect(() => {
    if (!questionId) return;
    setIsLoading(true);
    Promise.all([fetchMySolution(), fetchOthers(sortBy), fetchMetaData()]).finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId]);

  const handleSortChange = (nextSortBy) => {
    setSortBy(nextSortBy);
    setPage(1);
    fetchOthers(nextSortBy, 1);
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    fetchOthers(sortBy, nextPage);
  };

  const handleListScroll = () => {
    if (!hasScrolledList) setHasScrolledList(true);
  };

  const handleOpenAdd = () => setModalMode('new');
  const handleOpenEdit = () => setModalMode('edit');
  const handleCloseModal = () => setModalMode(null);

  const handleSubmitSolution = async (blobs) => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      blobs.forEach((blob, index) =>
        formData.append('images', blob, `solution-${index + 1}.${getFileExtensionForBlob(blob)}`),
      );

      if (modalMode === 'edit') {
        await axios.patch(`${BASE_URL}/${questionId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await axios.post(`${BASE_URL}/${questionId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        // webengage event
        window.webengage.track('community_solution_uploaded', {
          'User Id': user?.email || '',
          'Question Id': questionId,
          'Test Type': questionMeta?.testType || '',
          'Test Title': questionMeta?.testTitle || '',
          'Pack Title': questionMeta?.packTitle || '',
          Subject: questionMeta?.subjectTitle || '',
          Chapter: questionMeta?.chapter || '',
          Topic: questionMeta?.topic || '',
          Module: 'solutions',
        });
      }

      toast.success(modalMode === 'edit' ? 'Solution submitted for re-review' : 'Solution submitted for review');
      setModalMode(null);
      fetchMySolution();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVote = async (solutionId, vote) => {
    try {
      const res = await axios.post(`${BASE_URL}/${questionId}/${solutionId}/vote`, { vote });

      // webengage event
      window.webengage.track('community_solution_user_action', {
        'User Id': user?.email,
        'Question Id': questionId,
        'Solution Id': solutionId,
        'Test Type': questionMeta?.testType || '',
        'Test Title': questionMeta?.testTitle || '',
        'Pack Title': questionMeta?.packTitle || '',
        Subject: questionMeta?.subjectTitle || '',
        Chapter: questionMeta?.chapter || '',
        Topic: questionMeta?.topic || '',
        Module: 'solutions',
        'Action Type': vote === 'upvote' ? 'appreciated' : 'downvoted',
      });

      return res?.data?.data;
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to register your vote. Please try again.');
      throw error;
    }
  };

  const handleReport = async ({ reports, description }) => {
    setIsReporting(true);
    try {
      await axios.post(`${BASE_URL}/${questionId}/${reportSolutionId}/report`, { reports, description });

      // webengage event
      window.webengage.track('community_solution_user_action', {
        'User Id': user?.email,
        'Question Id': questionId,
        'Solution Id': reportSolutionId,
        'Test Type': questionMeta?.testType || '',
        'Test Title': questionMeta?.testTitle || '',
        'Pack Title': questionMeta?.packTitle || '',
        Subject: questionMeta?.subjectTitle || '',
        Chapter: questionMeta?.chapter || '',
        Topic: questionMeta?.topic || '',
        Module: 'solutions',
        'Action Type': 'reported',
      });

      toast.success('Reported. Our team will review this solution.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to report this solution.');
    } finally {
      setIsReporting(false);
      setReportSolutionId(null);
    }
  };

  if (isLoading) {
    return <div className="py-16 text-center text-[14px] text-[var(--q3-neutral-light)]">Loading...</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      {!hasWriteAccess && (
        <BlockedBanner reason={writeAccessBlockReason} userEmail={user?.email} className="max-w-[712px]" />
      )}

      {!mySolution && hasWriteAccess && (
        <div className="flex flex-row items-center justify-between gap-2 md:gap-3 rounded-xl bg-[var(--q3-surface-dim)] px-3 md:px-4 py-3 md:py-4 max-w-[712px] overflow-hidden">
          <div className="flex flex-row items-center gap-2 md:gap-3 min-w-0">
            <PencilSimpleLineIcon className="md:text-xl shrink-0 text-[var(--q3-neutral-default)]" />
            <div className="flex flex-col min-w-0">
              <span className="text-sm md:text-base font-semibold text-[var(--q3-neutral-default)] whitespace-nowrap">
                Add your Solution
              </span>
              <span className="hidden md:inline text-sm text-[var(--q3-neutral-light)]">
                Upload a photo of your worked-out solution
              </span>
            </div>
          </div>
          <Button
            onClick={handleOpenAdd}
            className="shrink-0 flex flex-row items-center gap-1 md:gap-2 bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)] text-xs md:text-sm font-medium px-3 md:px-4 py-1.5 md:py-2 rounded-full hover:scale-100 whitespace-nowrap"
          >
            Add Solution
            <ArrowRightIcon className="md:text-lg" />
          </Button>
        </div>
      )}

      {mySolution && (
        <MySolutionCard
          solution={mySolution}
          user={user}
          hasWriteAccess={hasWriteAccess}
          blockReason={writeAccessBlockReason}
          maxEditCount={metaData?.maxEditCount}
          onEdit={handleOpenEdit}
        />
      )}

      <div className="border border-[var(--q3-stroke-light)] rounded-xl overflow-hidden max-w-[712px]">
        <div className="px-4 py-3 border-b border-[var(--q3-stroke-light)]">
          <span className="text-sm font-medium text-[var(--q3-neutral-light)]">SOLUTION BY OTHER STUDENTS</span>
        </div>

        {others.length > 0 ? (
          <div className="flex flex-col">
            <div className="flex flex-row flex-nowrap items-center gap-2 px-4 pt-4 pb-2 overflow-x-auto">
              <span className="text-sm text-[var(--q3-neutral-light)] whitespace-nowrap shrink-0">Sort by</span>
              <button
                type="button"
                onClick={() => handleSortChange('votes')}
                className={`cursor-pointer whitespace-nowrap shrink-0 text-xs md:text-sm font-medium px-3 py-1.5 rounded-full ${
                  sortBy === 'votes'
                    ? 'bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)]'
                    : 'border border-[var(--q3-stroke-light)] text-[var(--q3-neutral-default)]'
                }`}
              >
                Most appreciated first
              </button>
              <button
                type="button"
                onClick={() => handleSortChange('createdAt')}
                className={`cursor-pointer whitespace-nowrap shrink-0 text-xs md:text-sm font-medium px-3 py-1.5 rounded-full ${
                  sortBy === 'createdAt'
                    ? 'bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)]'
                    : 'border border-[var(--q3-stroke-light)] text-[var(--q3-neutral-default)]'
                }`}
              >
                New first
              </button>
            </div>

            <div className="relative">
              <div
                ref={listRef}
                onScroll={handleListScroll}
                className="flex flex-col px-4 overflow-y-auto divide-y divide-[var(--q3-stroke-light)]"
                style={{ maxHeight: LIST_MAX_HEIGHT }}
              >
                {others.map((solution) => (
                  <OtherSolutionCard
                    key={solution._id}
                    solution={solution}
                    onVote={handleVote}
                    onReport={setReportSolutionId}
                  />
                ))}
              </div>

              {canScrollList && !hasScrolledList && (
                <div className="pointer-events-none absolute inset-x-0 bottom-3 flex items-center justify-center">
                  <span className="flex items-center gap-1 bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] text-[var(--q3-blue-normal)] text-sm font-medium px-3 py-1.5 rounded-full shadow-md">
                    Scroll To view more
                    <CaretDownIcon size={14} />
                  </span>
                </div>
              )}
            </div>

            {othersTotal > PAGE_SIZE && (
              <div className="flex flex-row items-center justify-center gap-3 px-4 py-4 border-t border-[var(--q3-stroke-light)]">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => handlePageChange(page - 1)}
                  className="cursor-pointer flex items-center justify-center h-8 w-8 rounded-full border border-[var(--q3-stroke-light)] text-[var(--q3-neutral-default)] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <CaretLeftIcon size={14} />
                </button>
                <span className="text-sm text-[var(--q3-neutral-default)]">
                  Page {page} of {Math.ceil(othersTotal / PAGE_SIZE)}
                </span>
                <button
                  type="button"
                  disabled={page >= Math.ceil(othersTotal / PAGE_SIZE)}
                  onClick={() => handlePageChange(page + 1)}
                  className="cursor-pointer flex items-center justify-center h-8 w-8 rounded-full border border-[var(--q3-stroke-light)] text-[var(--q3-neutral-default)] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <CaretRightIcon size={14} />
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
            <span className="text-[var(--q3-neutral-default)] font-semibold">No community solutions yet</span>
            <p className="text-sm text-[var(--q3-neutral-light)] max-w-[392px]">
              {hasWriteAccess
                ? 'Be the first to share how you solved this. Students who post approved solutions help thousands of others prepare.'
                : 'No students have shared a solution for this question yet. Check back later.'}
            </p>
            {!mySolution && hasWriteAccess && (
              <Button
                onClick={handleOpenAdd}
                className="flex flex-row items-center mt-1 gap-1 bg-[var(--q3-blue-normal)] text-[var(--q3-neutral-default_inverted)] text-sm font-medium px-5 py-2.5 rounded-full hover:bg-[var(--q3-blue-normal)]/90 hover:scale-100 duration-200"
              >
                <PlusIcon size={18} className="text-[var(--q3-neutral-default_inverted)]" />
                Add Solution
              </Button>
            )}
          </div>
        )}
      </div>

      {modalMode && (
        <AddEditSolutionModal
          open
          isEdit={modalMode === 'edit'}
          showReverifyWarning={modalMode === 'edit' && mySolution?.status === 'approved'}
          onClose={handleCloseModal}
          onSubmit={handleSubmitSolution}
          isSubmitting={isSubmitting}
          onViewGuidelines={() => setShowGuidelines(true)}
        />
      )}

      <GuidelinesModal
        open={showGuidelines}
        onClose={() => setShowGuidelines(false)}
        guidelines={metaData?.guideline}
      />

      <ReportSolutionModal
        open={!!reportSolutionId}
        onClose={() => setReportSolutionId(null)}
        reportOptions={metaData?.reportOptions}
        onSubmit={handleReport}
        isSubmitting={isReporting}
      />
    </div>
  );
};

export default CommunitySolutionPanel;
